import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ConfirmDialogProvider } from "./components/ui/confirm-dialog";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NotificationProvider } from "./context/NotificationContext";
import { PasswordChangeDialog } from "./components/ui/password-change-dialog";
import ErrorBoundary from "./components/ErrorBoundary";
import UpdatePrompt from "./components/UpdatePrompt";
import PasskeySetupPrompt from "./components/PasskeySetupPrompt";
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardPage from "./pages/DashboardPage";
import JobsPage from "./pages/JobsPage";
import JobDetailPage from "./pages/JobDetailPage";
import CreateJobPage from "./pages/CreateJobPage";
import EmployeesPage from "./pages/EmployeesPage";
import SettingsPage from "./pages/SettingsPage";
import ProfilePage from "./pages/ProfilePage";
import ReportsPage from "./pages/ReportsPage";
import HoursReportPage from "./pages/HoursReportPage";
import ProductivityReportPage from "./pages/ProductivityReportPage";
import DailyReportPage from "./pages/DailyReportPage";
import DailyEntryPage from "./pages/DailyEntryPage";
import ReceiptsPage from "./pages/ReceiptsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import BillingApplicationPage from "./pages/BillingApplicationPage";
import TrainingTrackingPage from "./pages/TrainingTrackingPage";
import ApplyPage from "./pages/ApplyPage";
import ApplicationsPage from "./pages/ApplicationsPage";
import ContactsPage from "./pages/ContactsPage";
import OnboardingForm from "./pages/OnboardingForm";
import OnboardingReviewPage from "./pages/OnboardingReviewPage";
import Layout from "./components/Layout";
import "@/App.css";

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Foreman goes to foreman page, others to dashboard
    if (user.role === "foreman") {
      return <Navigate to="/foreman" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user, loading, mustChangePassword, loginPassword, passwordChanged, getAuthHeaders, token } = useAuth();
  const location = useLocation();
  const [showPasskeyPrompt, setShowPasskeyPrompt] = useState(false);
  const [passkeySupported, setPasskeySupported] = useState(false);

  // Check WebAuthn support once on mount
  useEffect(() => {
    const checkWebAuthnSupport = async () => {
      if (!window.PublicKeyCredential) {
        setPasskeySupported(false);
        return;
      }
      
      try {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setPasskeySupported(available);
        console.log("WebAuthn platform authenticator available:", available);
      } catch (err) {
        console.log("WebAuthn check failed:", err);
        setPasskeySupported(false);
      }
    };
    
    checkWebAuthnSupport();
  }, []);

  // Show passkey prompt after successful login (when user navigates from login to a protected route)
  useEffect(() => {
    const justLoggedIn = sessionStorage.getItem("just_logged_in");
    
    // Only show if: user logged in, WebAuthn supported, not changing password, not already dismissed
    if (user && justLoggedIn === "true" && !mustChangePassword && passkeySupported) {
      // Check if already dismissed this session
      if (sessionStorage.getItem("passkey_prompt_dismissed") === "true") {
        sessionStorage.removeItem("just_logged_in");
        return;
      }
      
      // Delay to let the page settle
      const timer = setTimeout(() => {
        sessionStorage.removeItem("just_logged_in");
        setShowPasskeyPrompt(true);
      }, 2000);
      return () => clearTimeout(timer);
    } else if (user && justLoggedIn === "true" && !passkeySupported) {
      // WebAuthn not supported, just clear the flag
      sessionStorage.removeItem("just_logged_in");
    }
  }, [user, mustChangePassword, passkeySupported]);

  // Close passkey prompt handler
  const handleClosePasskeyPrompt = () => {
    setShowPasskeyPrompt(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <>
      {/* Password Change Dialog - shown when user must change password */}
      {user && mustChangePassword && (
        <PasswordChangeDialog
          open={true}
          onSuccess={passwordChanged}
          getAuthHeaders={getAuthHeaders}
          currentPassword={loginPassword}
        />
      )}
      
      {/* Passkey Setup Prompt - shown after successful password login on supported devices */}
      {showPasskeyPrompt && passkeySupported && (
        <PasskeySetupPrompt 
          show={true} 
          onClose={handleClosePasskeyPrompt} 
        />
      )}
      
      <Routes>
        <Route
          path="/login"
          element={user ? (
            user.role === "foreman" ? <Navigate to="/foreman" replace /> : <Navigate to="/dashboard" replace />
          ) : <LoginPage />}
        />
        
        {/* Public employment application page */}
        <Route
          path="/careers"
          element={<ApplyPage />}
        />
        {/* Legacy alias — keep old /apply links working */}
        <Route
          path="/apply"
          element={<Navigate to="/careers" replace />}
        />

        {/* Public new-hire onboarding (secure token link) */}
        <Route
          path="/onboarding/:token"
          element={<OnboardingForm />}
        />
        
        {/* Password reset page - public route */}
        <Route
          path="/reset-password"
          element={<ResetPasswordPage />}
        />
        
        {/* Daily Entry for Foreman - standalone without Layout */}
        <Route
          path="/foreman/*"
          element={
            <ProtectedRoute allowedRoles={["foreman"]}>
              <DailyEntryPage />
            </ProtectedRoute>
          }
        />
        
        {/* Main app routes with Layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute allowedRoles={["admin", "project_manager", "office_manager", "superintendent"]}>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="jobs" element={<JobsPage />} />
          <Route path="jobs/new" element={<CreateJobPage />} />
          <Route path="jobs/:jobId/edit" element={<CreateJobPage />} />
          <Route path="jobs/:id" element={<JobDetailPage />} />
          <Route path="jobs/:jobId/billing" element={<BillingApplicationPage />} />
          <Route path="employees" element={<EmployeesPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="reports/hours" element={<HoursReportPage />} />
          <Route path="reports/productivity" element={<ProductivityReportPage />} />
          <Route path="reports/training" element={<TrainingTrackingPage />} />
          <Route path="reports/daily" element={<DailyReportPage />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="daily-entry" element={<DailyEntryPage />} />
          <Route path="receipts" element={<ReceiptsPage />} />
          <Route path="applications" element={<ApplicationsPage />} />
          <Route path="contacts" element={<ContactsPage />} />
          <Route path="onboarding/review/:id" element={<OnboardingReviewPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Route>
        
        {/* Catch-all route - redirect based on role */}
        <Route path="*" element={<CatchAllRedirect />} />
      </Routes>
    </>
  );
}

// Helper component to redirect based on user role
const CatchAllRedirect = () => {
  const { user } = useAuth();
  if (user?.role === "foreman") {
    return <Navigate to="/foreman" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
            <ConfirmDialogProvider>
              <UpdatePrompt />
              <AppRoutes />
              <Toaster position="top-center" richColors closeButton />
            </ConfirmDialogProvider>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
